import { useEffect, useState, useContext } from "react";
import ShowMore from "react-show-more";
import { Oval } from "react-loader-spinner";
import { useParams, Link, useHistory } from "react-router-dom";
import { dataContext, userContext } from "../../Context";
import { Answer, Question, Status } from "../../Entities";
import { IAnswer } from "../../Interfaces";
import { updateSheetValues } from "../../service/googlesheetService";
import { IoIosArrowBack } from "react-icons/io";
import "./Form.css";

interface IQuestion {
  index: number;
  domain: string;
  majorSkill: string;
  detailSkill: string;
  description: string;
}

const STATUS_FORM = "StatusForm";

interface IParams {
  expertise: string;
  tribu: string;
}

const Form = () => {
  //React Hooks
  const [questions, setQuestions] = useState<Array<IQuestion>>([]);
  const [counter, setCounter] = useState(0);
  const [disable, setDisable] = useState(true);
  const [value, setValue] = useState("");
  const [resetAnswer, setResetAnswer] = useState(-1);
  const [loader, setLoader] = useState(false);
  const { answers, datas } = useContext(dataContext);
  const { user } = useContext(userContext);
  const history = useHistory();
  const { expertise, tribu } = useParams<IParams>();

  //Variable creation
  const isMobile = window.innerWidth <= 500;
  const firstLetter = 0;
  const dropFirstLetter = 1;
  const mobileDisplay = 4;
  const desktopDisplay = 10;
  const question = questions[counter];
  const questionsLength = questions?.length;
  const seeMore = "Voir plus";
  const seeLess = "Voir moins";
  const lastQuestion = counter + 1 === questionsLength;
  const status = lastQuestion ? "done" : "in_progress";
  const buttonValue = lastQuestion ? "Sauvegarde" : "suivant";
  const answerForGS = new Answer(
    tribu,
    user?.googleId,
    user?.family_name,
    user?.given_name,
    user?.name,
    user?.email,
    question?.domain,
    question?.majorSkill,
    question?.detailSkill,
    value
  );
  const answerForGSParsed = answerForGS.getAnswer();
  const statusObject = new Status(
    question?.domain,
    status,
    questionsLength,
    counter + 1
  );
  const statusArray = statusObject.getArrayStatus();
  const storage = localStorage.getItem("StatusForm");
  const token = localStorage.getItem("idToken")

  const parseQuestion = (rawDatas: Set<string>) => {
    let parseExpertise = expertise.replace("_", " ");

    const questionList = Array.from(rawDatas)
      .filter((question) => {
        const domain = question[0];
        return domain === parseExpertise;
      })
      .map((question, index) => {
        const domain = question[0];
        const majorSkill = question[1];
        const detailSkill = question[2];
        const description = question[3];
        return new Question(
          index + 1,
          domain,
          majorSkill,
          detailSkill,
          description
        );
      });

    setQuestions(questionList);
  };

  //Get the last question filled in the localstorage
  const getLastQuestionNumber = (storage: string | null): number | null => {
    let jsonObject: Status[] = [];

    if (storage) {
      jsonObject = JSON.parse(storage);
    }

    const questions = jsonObject.find(
      (item: Status) => item.expertise === expertise
    );

    if (questions) {
      let lastQuestion: number =
        questions.lastAnswerCount === 0 ? 0 : questions.lastAnswerCount;

      return lastQuestion;
    }
    return null;
  };

  // First letter capitalized
  const capitalizeFirstLetter = (str: string) => {
    if (str) {
      return str
        .toLowerCase()
        .split(" ")
        .map(
          (word) =>
            word.charAt(firstLetter).toUpperCase() + word.slice(dropFirstLetter)
        )
        .join(" ");
    }
  };

  // Split '\n' into creating new lines for Description skill
  const newDescription: any = question?.description
    .split("\n")
    .map(function (item, key) {
      return (
        <span key={key}>
          {item}
          <br />
        </span>
      );
    });

  const selectAnswer = (key: number, answer: IAnswer) => {
    setDisable(false);
    setResetAnswer(key);
    setValue(answer[0]);
  };

  useEffect(() => {
    const lastQuestion = getLastQuestionNumber(storage);
    if (!user) {
      history.push("/");
    }
    if (lastQuestion) {
      setCounter(lastQuestion);
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    parseQuestion(datas);

    // eslint-disable-next-line
  }, [datas]);

  const sendDataToGS = async (dataToSend: IAnswer) => {
    const responseDataSend = await updateSheetValues(token, user.googleId, dataToSend);
    if (responseDataSend === 200) {
      setLoader(false);
      setCounter((prev) => {
        return prev + 1;
      });
      window.scrollTo(0, 0);
      if (lastQuestion) {
        if (statusArray) {
          statusObject.lastAnswerCount = counter + 1;
          statusObject.completion = 100;
          const newStorage = [...statusArray, statusObject];
          localStorage.setItem(STATUS_FORM, JSON.stringify(newStorage));
        }
        history.push(`/tribu/${tribu}`);
      }
    }
  };

  const submitAnswer = (data: IAnswer) => {
    setResetAnswer(-1);
    setDisable(true);
    updateSheetValues(token, "Reponses_BPM!A:J", data);
    sendDataToGS(data);
  };

  const handleOnClick = () => {
    if (statusArray) {
      setLoader(true);
      const newStorage = [...statusArray, statusObject];
      localStorage.setItem(STATUS_FORM, JSON.stringify(newStorage));
    }

    submitAnswer(answerForGSParsed);
  };

  return (
    <div className="form" id="top">
      <div className="question">
        <div className="headerBorder">
          <div className="questionHeader">
            <div className="backButton">
              <Link to={`/tribu/${tribu}`}>
                {isMobile ? (
                  <IoIosArrowBack />
                ) : (
                  <p>
                    <IoIosArrowBack /> Sauvegarder et quitter
                  </p>
                )}
              </Link>
            </div>
            <div className="titleHeader">
              <p className="questionDomain">
                <strong>{capitalizeFirstLetter(question?.domain)}</strong>
              </p>
              <p className="questionMajorSkill">{question?.majorSkill}</p>
            </div>
          </div>
        </div>

        <div className="borderSeperation"></div>
        <div className="questionBody">
          <p className="questionDetailSkill">
            {" "}
            <strong>{question?.detailSkill}</strong>
          </p>
          <div className="questionDescription">
            <ShowMore
              lines={isMobile ? mobileDisplay : desktopDisplay}
              more={seeMore}
              less={seeLess}
              anchorClass="my-anchor-class"
            >
              {newDescription}
            </ShowMore>
          </div>
        </div>
      </div>
      <div className="answersBackground">
        <div className="answers">
          {answers.map((answer: IAnswer, key: number) => {
            return (
              <div
                className="answer"
                key={answer[0]}
                style={{
                  background: resetAnswer === key ? "#efeeee" : "",
                  border:
                    resetAnswer === key ? "4px solid rgb(148,148,148)" : "",
                  boxShadow:
                    resetAnswer === key ? "0px 8px 0px 0px #efeeee" : "",
                }}
                onClick={() => {
                  selectAnswer(key, answer);
                }}
              >
                <p className="answerIcon">{answer[1]}</p>
                <p className="answerText">{answer[2]}</p>
              </div>
            );
          })}
        </div>
        <div className="footerQuestion">
          <p>
            Question {counter + 1} sur {questionsLength}
          </p>
          <button
            className="buttonNext"
            type="submit"
            value={buttonValue}
            disabled={disable}
            onClick={() => {
              handleOnClick();
            }}
          >
            <Oval
              height={20}
              width={20}
              color="#F8485E"
              secondaryColor="white"
              wrapperStyle={{ alignItems: "center" }}
              wrapperClass=""
              visible={loader}
              ariaLabel="oval-loading"
              strokeWidth={15}
              strokeWidthSecondary={13}
            />
            {buttonValue}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Form;
